import { MentorshipProgramProfileAnswer } from 'redux/schemas/models/mentoring-program-profile-answers';
import { OrgLevelQuestion, ProgramLevelQuestion } from 'redux/schemas/models/mentoring-program-profile-questions';
import t from 'react-translate';
import {
  TIMEZONE_QUESTION_TEXT,
} from 'athena/components/mentoring-program/program-configuration/program-profile-setup/org-level';
import { AngularContext } from 'react-app';
import { useContext } from 'react';

type OrgFieldProps = {
  field: MentorshipProgramProfileAnswer;
};

const isQuestionTimeZone = (question: OrgLevelQuestion | ProgramLevelQuestion) => {
  const questionList = (question as ProgramLevelQuestion)?.questionList;
  return questionList?.questionText === TIMEZONE_QUESTION_TEXT;
};

const getFormattedTimezone = (answer: string) => {
  const { injectServices } = useContext(AngularContext);
  const [TimezonesService] = injectServices(['TimezonesService']);
  const timeZone = TimezonesService.timezones.find((tz) => tz.longName === answer);

  const translation = t.TIMEZONE[timeZone?.translationKey]?.();
  
  let formattedOffset = timeZone?.offset;

  if (formattedOffset) {
    const match = formattedOffset.match(/\(UTC([+-])(\d{2}):(\d{2})\)/);
    if (match) {
      const [, sign, hours] = match;
      formattedOffset = `UTC ${sign} ${hours}`;
    }
  }

  return `${translation} - ${formattedOffset}`;
};

const getQuestionDetails = (field: MentorshipProgramProfileAnswer) => {
  const { profileQuestion } = field;
  const isTimeZoneQuestion = isQuestionTimeZone(profileQuestion);
  const profileSetting = (profileQuestion as OrgLevelQuestion)?.profileSetting;

  const questionText = isTimeZoneQuestion
    ? t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.HEADERS.TIME_ZONE()
    : profileSetting?.name;
  const isHidden = isTimeZoneQuestion ? !field?.isRequired : profileSetting?.isHidden;
  return { questionText, isHidden };
};

const getAnswerText = (
  question: OrgLevelQuestion | ProgramLevelQuestion,
  answer: string
) => isQuestionTimeZone(question) ? getFormattedTimezone(answer) : answer;

const OrgField = ({ field }: OrgFieldProps) => {
  const { profileQuestion, answer } = field || {};
  const { questionText, isHidden } = getQuestionDetails(field);

  return (
    answer && !isHidden && (
      <>
        <div className='gray-2 text-small mb-1'>
          {questionText}
        </div>
        <div className='mb-1 text-regular heading-5'>
          {getAnswerText(profileQuestion, (answer as string))}
        </div>
      </>
    )
  );
};

type OrgLevelInfoProps = {
  endRef: (node?: Element | null) => void;
  orgInfo: MentorshipProgramProfileAnswer[];
};

const OrgLevelInfo = ({ endRef, orgInfo }: OrgLevelInfoProps) => {
  const hasAnswers = orgInfo?.some((item) => item?.answer);

  return (
    hasAnswers ? (
      <>
        <div className='px-5 py-4 info-container'>
          {orgInfo?.map(field => (<OrgField key={field.id} field={field} />))}
        </div>
        <div ref={endRef} />
      </>
    ) : null
  );
};

export default OrgLevelInfo;
